import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, type CodigoConta, type LeituraMl, type LeituraRegistrada } from "../api";
import { playFeedback } from "../feedback";
import { startScanning, type StopScanning } from "../scanner";

/// Tela do Mercado Livre no aplicativo: o encontro com o motorista.
///
/// Vale para COLETA, devolução e qualquer serviço do Mercado Livre que peça o
/// código de confirmação — é o mesmo QR e a mesma página de handshake; o que
/// muda é o que o motorista leva ou traz.
///
/// Cada conta (Lucas, Yago, Renato) tem o PRÓPRIO código — são painéis
/// diferentes no Mercado Livre. A tela mostra os três, e o motorista de cada
/// conta lê o que é dele.
///
/// O botão de abrir leva à MESMA página do QR com o código já copiado, pronto
/// para colar. Por que copiar e não preencher sozinho: a página é do
/// mercadolivre.com.br, e o navegador proíbe um site de escrever dentro de
/// outro — o que dá para eliminar é a busca do código, não o toque de colar.

/// Copia para a área de transferência. Devolve `false` quando o navegador
/// recusa — acontece fora de HTTPS, e aí a tela precisa dizer isso em vez de
/// fingir que copiou.
async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/// Uma conta, um código, tocável para copiar. `onAtualizado`, quando
/// passado, também deixa cadastrar/corrigir o código ali mesmo — usado na
/// tela principal, não no painel da câmera, que é só para copiar rápido.
function LinhaCodigo({
  codigo,
  onAtualizado,
}: {
  codigo: CodigoConta;
  onAtualizado?: (novo: CodigoConta) => void;
}) {
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState("");
  const [salvando, setSalvando] = useState(false);

  const copiar = async () => {
    if (!codigo.code) return;
    if (await copiarTexto(codigo.code)) {
      setCopiado(true);
      setErro(null);
      setTimeout(() => setCopiado(false), 1500);
    } else {
      setErro("Não foi possível copiar. O código está na tela.");
    }
  };

  const salvar = async () => {
    const valor = texto.trim().toUpperCase();
    if (!valor) return;

    setSalvando(true);
    setErro(null);
    try {
      const r = await api.definirCodigoDoDia(codigo.accountId, valor);
      const atualizado = r.codigos.find((c) => c.accountId === codigo.accountId);
      if (atualizado) onAtualizado?.(atualizado);
      setEditando(false);
      setTexto("");
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível salvar o código.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="codigo-conta">
      <div className="nome">
        <span className="ponto" style={{ background: codigo.color ?? "var(--dep-border)" }} />
        {codigo.accountName}
      </div>

      {editando ? (
        <div className="digitar-codigo">
          <input
            autoFocus
            value={texto}
            placeholder="Ex.: CA5FAE0D"
            maxLength={24}
            autoCapitalize="characters"
            onChange={(e) => setTexto(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && void salvar()}
          />
          <button className="btn-deliver" disabled={salvando} onClick={() => void salvar()}>
            {salvando ? "…" : "Salvar"}
          </button>
          <button className="btn-ghost" onClick={() => setEditando(false)}>
            Cancelar
          </button>
        </div>
      ) : codigo.code ? (
        <button className="valor" onClick={() => void copiar()} title="Tocar para copiar">
          {codigo.code}
          <span className="copiar-dica">{copiado ? "copiado" : "tocar para copiar"}</span>
        </button>
      ) : (
        <div className="valor aguardando">não cadastrado</div>
      )}

      {onAtualizado && !editando && (
        <button className="btn-ghost pequeno" onClick={() => { setTexto(codigo.code ?? ""); setEditando(true); }}>
          {codigo.code ? "Alterar" : "Cadastrar"}
        </button>
      )}

      {erro && <p className="note erro">{erro}</p>}
    </div>
  );
}

export function MercadoLivre() {
  const [codigos, setCodigos] = useState<CodigoConta[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [leiturasHoje, setLeiturasHoje] = useState<LeituraMl[]>([]);
  const [camera, setCamera] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [c, lista] = await Promise.all([api.codigosDoDia(), api.leiturasDeHoje()]);
      setCodigos(c.codigos);
      setLeiturasHoje(lista.rows);
    } catch {
      // Sem rede a tela ainda serve para abrir a câmera; o erro aparece na hora
      // de registrar, que é quando ele de fato atrapalha.
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  return (
    <main>
      <h2 className="section">Mercado Livre</h2>
      <p className="note">
        Cada conta tem o próprio código de autorização — é o que libera a coleta, a
        devolução e os demais serviços com o motorista daquela conta.
      </p>

      {carregando ? (
        <p className="note">Carregando…</p>
      ) : codigos.length === 0 ? (
        <p className="note">Nenhuma conta do Mercado Livre cadastrada ainda.</p>
      ) : (
        <div className="codigos-contas">
          {codigos.map((c) => (
            <LinhaCodigo
              key={c.accountId}
              codigo={c}
              onAtualizado={(novo) =>
                setCodigos((prev) => prev.map((x) => (x.accountId === novo.accountId ? novo : x)))
              }
            />
          ))}
        </div>
      )}

      <div className="actions">
        <button className="btn primary" onClick={() => setCamera(true)}>
          Ler QR do Mercado Livre
        </button>
      </div>

      <p className="note">
        Toque quando o motorista chegar — coleta ou devolução — e leia o QR que ele
        apresenta. A leitura fica registrada com hora e operador, e os três códigos
        aparecem na tela, prontos para copiar e colar na página do Mercado Livre.
      </p>

      {erro && <p className="note erro">{erro}</p>}

      {leiturasHoje.length > 0 && (
        <>
          <h2 className="section">Leituras de hoje · {leiturasHoje.length}</h2>
          <div className="batch">
            {leiturasHoje.map((c) => (
              <div key={c.id} className="batch-item">
                <div className="top">
                  <code>{c.reference ?? "sem identificador"}</code>
                  <span className={`pill ${c.format === "ML_HANDSHAKE" ? "ok" : "unknown"}`}>
                    {hora(c.createdAt)}
                  </span>
                </div>
                <div className="who">
                  {c.format === "ML_HANDSHAKE" ? "Mercado Livre" : "QR não reconhecido"}
                  {c.logistic && ` · ${c.logistic}`} · {c.by}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {camera && (
        <LeitorHandshake
          onClose={() => {
            setCamera(false);
            void recarregar();
          }}
          onErro={setErro}
        />
      )}
    </main>
  );
}

/// Câmera de tela cheia dedicada ao QR do motorista.
///
/// Separada da tela de bipar pacote de propósito: são leituras com significados
/// diferentes, e uma câmera só, que às vezes cadastra pacote e às vezes registra
/// handshake, transformaria um QR lido na hora errada em pacote fantasma.
function LeitorHandshake({
  onClose,
  onErro,
}: {
  onClose: () => void;
  onErro: (mensagem: string | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<LeituraRegistrada | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Enquanto o resultado está na tela, a câmera continua ligada mas as leituras
  // são ignoradas: o operador está lendo o código, e um segundo QR no campo de
  // visão trocaria a tela debaixo dos olhos dele.
  const ocupado = useRef(false);

  const registrar = useCallback(async (raw: string) => {
    if (ocupado.current) return;
    ocupado.current = true;
    setEnviando(true);
    setErro(null);

    try {
      const r = await api.registrarLeitura(raw);
      playFeedback(
        r.repetido ? "DUPLICATE" : r.leitura.format === "UNKNOWN" ? "UNIDENTIFIED" : "OK"
      );
      setResultado(r);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível registrar a leitura.");
      ocupado.current = false;
    } finally {
      setEnviando(false);
    }
  }, []);

  useEffect(() => {
    let stop: StopScanning | undefined;
    let cancelado = false;

    void (async () => {
      const video = videoRef.current;
      if (!video) return;
      const fn = await startScanning(video, (texto) => void registrar(texto), setErro);
      if (cancelado) fn();
      else stop = fn;
    })();

    return () => {
      cancelado = true;
      stop?.();
    };
  }, [registrar]);

  const novaLeitura = () => {
    setResultado(null);
    setErro(null);
    ocupado.current = false;
  };

  // Direto no <body>: esta câmera é chamada de dentro do <main> da tela de
  // Mercado Livre, que rola (`overflow-y: auto`). No Safari do iPhone, um
  // `position: fixed` preso dentro de um contêiner que rola fica cortado
  // pelos limites daquele contêiner em vez de cobrir a tela toda — o menu de
  // baixo aparecia por cima da câmera. O portal tira a árvore de onde ela foi
  // chamada e bota no <body>, imune ao aninhamento.
  return createPortal(
    <div className="scan-screen">
      <video ref={videoRef} muted playsInline />

      <div className="viewfinder" aria-hidden="true">
        <span className="c tl" />
        <span className="c tr" />
        <span className="c bl" />
        <span className="c br" />
      </div>

      <button
        className="scan-back"
        onClick={() => {
          onErro(null);
          onClose();
        }}
        aria-label="Voltar"
      >
        ←
      </button>

      <div className="scan-hint">{resultado ? "Leitura registrada" : "Leia o QR do motorista"}</div>

      {enviando && <div className="scan-warn">Registrando…</div>}

      {!resultado && erro && <div className="scan-warn erro">{erro}</div>}

      {resultado && (
        <div className="chegada-painel" role="dialog" aria-label="Leitura registrada">
          {resultado.repetido && (
            <div className="aviso repetido">
              Esta leitura já tinha sido registrada hoje às {hora(resultado.leitura.createdAt)} por{" "}
              {resultado.leitura.by}.
            </div>
          )}

          {resultado.leitura.format === "UNKNOWN" && (
            <div className="aviso desconhecido">
              QR fora do formato do Mercado Livre. A chegada foi registrada assim mesmo,
              com o texto lido.
            </div>
          )}

          <div className="rotulo">Códigos de autorização de hoje</div>

          <div className="codigos-contas compacto">
            {resultado.codigos.map((c) => (
              <LinhaCodigo key={c.accountId} codigo={c} />
            ))}
          </div>

          {/* Link, e não botão com window.open: navegação por <a> não é
              barrada por bloqueador de pop-up. */}
          {resultado.leitura.format === "ML_HANDSHAKE" && (
            <>
              <a
                className="btn-abrir"
                href={resultado.leitura.raw}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir no Mercado Livre
              </a>
              <p className="explica">
                Abre a página do QR. Toque no código da conta certa acima para copiar, e cole
                no campo — o Mercado Livre não deixa outro aplicativo escrever ali por fora.
              </p>
            </>
          )}

          {resultado.leitura.reference && (
            <div className="detalhe">
              Coleta {resultado.leitura.reference}
              {resultado.leitura.logistic && ` · ${resultado.leitura.logistic}`}
            </div>
          )}

          {erro && <div className="aviso desconhecido">{erro}</div>}

          <div className="acoes">
            <button className="btn-ghost" onClick={novaLeitura}>
              Ler outro
            </button>
            <button
              className="btn-deliver"
              onClick={() => {
                onErro(null);
                onClose();
              }}
            >
              Concluir
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
